import { recordOfflinePlay } from '../../helpers/utils/analyticsOffline';
import { trackEvent, trackProps } from '../../helpers/utils/analytics';
import {
  lazy,
  startTransition,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import { useFormik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { getLastScoreLimit } from '../../helpers/utils/recentScoreLimits';
import { GlobalContext } from '../../store/global-context';
import { getPersistedViewerSession } from '../../helpers/utils/viewerSession';
import { getUiMode } from '../../helpers/utils/featureFlags';
import {
  hasPlayerNamesEntered,
  hasRoundProgress,
} from '../../helpers/math/spadesMath';

import TeamNameInput from './TeamNameInput';
import PlayerNameInput from './PlayerNameInput';
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  TableSurface,
  FitStage,
  WideColumns,
  DealerTag,
} from '../ui';
import { useScreenLayout } from '../../helpers/utils/useScreenLayout';
import GameSetupRail from './GameSetupRail';
import DealerHandoff from '../game/DealerHandoff';
import { useKeyboardReveal } from '../../helpers/utils/useKeyboardReveal';
import { screenRect } from '../../helpers/utils/scrollFrame';
import {
  HOME_LOWER_BAND,
  HOME_SEATS,
  HOME_SEAT_IDS,
  HomeFeltPrint,
} from './homeTable';
import type { Names } from '../../types';
import {
  clearNameDraft,
  getNameDraft,
  sameNames,
  setNameDraft,
} from '../../helpers/utils/storage';
import {
  initialNames,
  initialFirstDealerOrder,
} from '../../helpers/utils/constants';
import {
  TITLE_TO_TEAM_NAMES,
  TEAM_NAME_GAP,
  TEAM_NAMES_TO_TABLE,
} from './figmaLayout';

const MAX_LIMIT_DIGITS = 4;
/**
 * Room kept between a seat field and the keyboard. (It once held the
 * "15 characters max" pill, which now sits on the seat's label row.)
 */
const SEAT_KEYBOARD_GAP = 28;

/** What must stay above the keyboard: the seat's label to just under its field.
 *  Screen px (screenRect), as the visual viewport it's compared with: the
 *  seats sit inside the zoomed tablet and desktop stage. */
const seatRegion = (input: HTMLInputElement) => {
  const seat = input.closest<HTMLElement>('[data-seat]') ?? input;
  return {
    top: screenRect(seat).top,
    bottom: screenRect(input).bottom + SEAT_KEYBOARD_GAP,
  };
};

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.matches('input, textarea, select, [contenteditable="true"]') ||
    target.closest('[role="dialog"]') != null);

const loadWarningModal = () => import('../modals/WarningModal');
const loadScoreLimitModal = () => import('../modals/ScoreLimitModal');
const WarningModal = lazy(loadWarningModal);
const ScoreLimitModal = lazy(loadScoreLimitModal);

const PLAYER_NAME_FIELDS = [
  't1p1Name',
  't2p1Name',
  't1p2Name',
  't2p2Name',
] as const;

// The four seats only need "present or not". Doing that here means a failed
// download can never be mistaken for a valid form.
function missingPlayerNames(values: Names) {
  const errors: Partial<Record<(typeof PLAYER_NAME_FIELDS)[number], string>> =
    {};
  for (const field of PLAYER_NAME_FIELDS) {
    if (!values[field]) errors[field] = 'Name needed';
  }
  return errors;
}

function NameForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const newGameSetup = location.state?.newGameSetup === true;
  const {
    role,
    roundHistory,
    currentRound,
    names,
    setNames,
    setScoreLimit,
    firstDealerOrder,
    startNewGame,
    setFirstDealerOrder,
  } = useContext(GlobalContext);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [isScoreLimitModalOpen, setIsScoreLimitModalOpen] = useState(false);
  const [pendingNames, setPendingNames] = useState<Names | null>(null);
  // C8: desktop Home's Game setup rail replaces the post-Start score-limit
  // dialog. Empty means no limit (decided in chat 14).
  const hasSetupRail = useScreenLayout() === 'desktop';
  // A picked preset is a whole number: the next digit typed starts a new one.
  const [setup, setSetup] = useState(() => {
    const limit = getLastScoreLimit();
    return {
      limit: limit === null ? '' : String(limit),
      preset: limit !== null,
    };
  });
  const setupLimit = setup.limit;
  const pressSetupDigit = (digit: string) =>
    setSetup((prev) => ({
      limit: ((prev.preset ? '' : prev.limit) + digit)
        .replace(/^0+(?=\d)/, '')
        .slice(0, MAX_LIMIT_DIGITS),
      preset: false,
    }));
  const pressSetupBackspace = () =>
    setSetup((prev) => ({ limit: prev.limit.slice(0, -1), preset: false }));

  // Typing a name with the phone keyboard up: the table never moves or
  // resizes; the page scrolls the field just above the keyboard instead.
  const formRef = useRef<HTMLFormElement>(null);
  const regionOf = useCallback(
    (input: HTMLInputElement) =>
      HOME_SEAT_IDS.includes(input.id) ? seatRegion(input) : screenRect(input),
    [],
  );
  const keyboardRoom = useKeyboardReveal(formRef, regionOf);

  const hasGameData =
    !newGameSetup &&
    (hasPlayerNamesEntered(names) ||
      hasRoundProgress(roundHistory, currentRound));

  // Who deals first is new-game setup (2026-10-26): the first game, and
  // Different teams. A saved game leaves it to the board's D.
  const showFirstDealer = !hasGameData;
  // Different teams keeps the saved game untouched until Start, so the first
  // dealer is held here and applied then. You deal first unless changed.
  const [setupDealer, setSetupDealer] = useState(initialFirstDealerOrder[0]);
  const firstDealer = newGameSetup
    ? setupDealer
    : currentRound?.dealerOverride || firstDealerOrder[0];
  const dealerSetup = newGameSetup
    ? { dealerId: setupDealer, onSelect: setSetupDealer }
    : undefined;

  // The modals are lazy: opening one in a transition keeps the screen as it
  // is until its code is in, instead of showing the Suspense fallback, which
  // React then holds for 300ms before the modal can appear.
  const handleNewGame = () => {
    startTransition(() => setIsWarningModalOpen(true));
  };

  // Names typed but not yet saved (Start / Continue) come back after a
  // refresh, as long as the saved names they were typed over haven't changed.
  const [draft] = useState(getNameDraft);
  const formNames = newGameSetup ? initialNames : names;
  const restored =
    draft && sameNames(draft.base, formNames) ? draft.values : undefined;

  const formik = useFormik<Names>({
    initialValues: {
      team1Name: restored?.team1Name ?? formNames.team1Name,
      team2Name: restored?.team2Name ?? formNames.team2Name,
      t1p1Name: restored?.t1p1Name ?? formNames.t1p1Name,
      t2p1Name: restored?.t2p1Name ?? formNames.t2p1Name,
      t1p2Name: restored?.t1p2Name ?? formNames.t1p2Name,
      t2p2Name: restored?.t2p2Name ?? formNames.t2p2Name,
    },
    validate: (values) => {
      const errors = missingPlayerNames(values);
      if (Object.keys(errors).length > 0) return errors;
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!hasGameData && hasSetupRail) {
        // The rail's limit applies on Start; a 0 can't (its caption says why).
        const limit = setupLimit === '' ? null : parseInt(setupLimit, 10);
        if (limit !== null && limit <= 0) return;
        setNames(values);
        if (limit !== null) {
          trackEvent('score_limit_confirmed', { context: 'setup' });
        }
        startGame(limit, values);
        return;
      }
      if (!newGameSetup) setNames(values);
      if (!hasGameData) {
        // Fresh game: offer an optional score limit before heading to the board.
        setPendingNames(values);
        startTransition(() => setIsScoreLimitModalOpen(true));
        return;
      }
      navigate('/spades-calculator', { state: values });
      trackEvent('game_continued', { ui_mode: getUiMode() });
    },
  });

  const startGame = (scoreLimit: number | null, startNames: Names | null) => {
    if (newGameSetup) {
      if (startNames) setNames(startNames);
      startNewGame(scoreLimit);
      // The deal goes clockwise from the chosen first dealer.
      const first = initialFirstDealerOrder.indexOf(setupDealer);
      setFirstDealerOrder([
        ...initialFirstDealerOrder.slice(first),
        ...initialFirstDealerOrder.slice(0, first),
      ]);
      trackEvent('game_reset', { source: 'home', teams: 'different' });
    } else setScoreLimit(scoreLimit);
    clearNameDraft();
    trackEvent('game_started', { ui_mode: getUiMode() });
    recordOfflinePlay('games', role);
    navigate('/spades-calculator', { state: startNames ?? undefined });
  };

  // The modal stays open: the board replaces Home, modal and all, so Home
  // doesn't show again for a moment on the way.
  const finishGameStart = (scoreLimit: number | null) => {
    startGame(scoreLimit, pendingNames);
    setPendingNames(null);
  };

  // The rail takes digits and Backspace from the keyboard too, unless a field
  // or a dialog has the keys.
  useEffect(() => {
    if (!hasSetupRail) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) {
        return;
      }
      if (/^[0-9]$/.test(e.key)) pressSetupDigit(e.key);
      else if (e.key === 'Backspace') pressSetupBackspace();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasSetupRail]);

  // A viewer is only here to watch a live board, not to (re)name players. If
  // this device has an active viewer session, bounce straight back to the board
  // so they can't edit names locally (which wouldn't sync to the leader anyway).
  useEffect(() => {
    const persisted = getPersistedViewerSession();
    if (persisted) {
      navigate(`/spades-calculator?session=${persisted}`, { replace: true });
    }
  }, [navigate]);

  const { values, setFieldValue } = formik;
  const { team1Name, team2Name } = values;

  useEffect(() => {
    if (sameNames(values, formNames)) clearNameDraft();
    else setNameDraft({ base: formNames, values });
  }, [values, formNames]);

  useEffect(() => {
    if (team1Name === '') {
      setFieldValue('team1Name', 'Team 1');
    }
    if (team2Name === '') {
      setFieldValue('team2Name', 'Team 2');
    }
  }, [team1Name, team2Name, setFieldValue]);
  const form = (
    <form
      ref={formRef}
      onSubmit={(e) => {
        trackEvent(
          hasGameData ? 'game_continue_requested' : 'game_start_requested',
          hasGameData ? { source: 'home' } : undefined,
        );
        formik.handleSubmit(e);
      }}
    >
      {/* The fit rule (S3 / S4): the table gives up height, then the
            screen scales, the same as on the board. */}
      <FitStage>
        {/* Emerald/Home (1659:21055). The Figma frame is edge to edge, so the
            content steps out of .App's 8pt page padding on phones. */}
        <Box mx={{ base: '-8px', md: 0 }} pb="var(--spades-spacing-xl)">
          <Text
            as="h1"
            mt="var(--spades-spacing-sm)"
            textAlign="center"
            color="spades.ivory"
            fontFamily="var(--spades-font-display)"
            fontSize="var(--spades-font-size-2xl)"
            fontWeight="400"
            lineHeight={1.25}
          >
            {hasGameData ? 'WELCOME BACK' : "WHO'S PLAYING?"}
          </Text>

          {/* Team names: each centred in its half, VS in the gap. */}
          <Flex
            mt={TITLE_TO_TEAM_NAMES}
            px="var(--spades-spacing-md)"
            gap={TEAM_NAME_GAP}
            align="center"
            height="32px"
          >
            <Box flex="1 1 0" minW={0}>
              <TeamNameInput
                id="team1Name"
                teamClassName="team1"
                teamName={formik.values.team1Name}
                handleChange={formik.handleChange}
              />
            </Box>
            <Text
              color="spades.muted"
              fontSize="var(--spades-font-size-2xs)"
              fontWeight="700"
              lineHeight={1.25}
              letterSpacing="var(--spades-tracking-md)"
            >
              VS
            </Text>
            <Box flex="1 1 0" minW={0}>
              <TeamNameInput
                id="team2Name"
                teamClassName="team2"
                teamName={formik.values.team2Name}
                handleChange={formik.handleChange}
              />
            </Box>
          </Flex>

          {/* The table, with each name field at its seat. */}
          <TableSurface mt={TEAM_NAMES_TO_TABLE}>
            {/* DOM order is the tab order, clockwise: You, Left, Partner,
                  Right. A changed first dealer glides to the new seat, as on
                  the board; the count never changes here. */}
            <DealerHandoff count={0}>
              {HOME_SEATS.map((seat) => {
                const dealerId = `${seat.team}BidsAndMade.${seat.id.includes('p1') ? 'p1' : 'p2'}Bid`;
                // An opponent's D ends the label row, the label on its outer side.
                const labelSide =
                  seat.dealer.place.right === '0' ? 'left' : 'right';
                return (
                  <Box
                    key={seat.id}
                    data-seat={seat.id}
                    data-deal-seat={dealerId}
                    position="absolute"
                    {...seat.place}
                    // While the "15 characters max" hint holds an opponent's
                    // label row, its D steps aside rather than poke out of it.
                    css={
                      seat.dealer.inLabelRow
                        ? {
                            '&:has([role="status"]) > [data-dealer-chip]': {
                              visibility: 'hidden',
                            },
                          }
                        : undefined
                    }
                  >
                    {showFirstDealer && firstDealer === dealerId && (
                      // The tap target reaches 9pt past the disc but stops
                      // 3pt short of the field, and short of the label (whose
                      // tap focuses the field).
                      <Box
                        data-dealer-chip
                        position="absolute"
                        display="flex"
                        zIndex={2}
                        {...seat.dealer.place}
                        css={{
                          '& [data-testid="dealerBadge"]': {
                            position: 'relative',
                            margin: 0,
                          },
                          '& [data-testid="dealerBadge"]::before': {
                            content: '""',
                            position: 'absolute',
                            inset: !seat.dealer.inLabelRow
                              ? '-9px -5px -9px -9px'
                              : labelSide === 'left'
                                ? '-9px -9px 0 -1px'
                                : '-9px -1px 0 -9px',
                            borderRadius: 'var(--spades-radius-pill)',
                          },
                        }}
                      >
                        <DealerTag
                          id={firstDealer}
                          isCurrent
                          roundHistory={roundHistory}
                          names={formik.values}
                          setup={dealerSetup}
                        />
                      </Box>
                    )}
                    <PlayerNameInput
                      teamClassName={seat.team}
                      id={seat.id}
                      label={seat.label}
                      seatLabel={seat.seatLabel}
                      hintOffset={
                        seat.id === 't2p1Name'
                          ? '-3px'
                          : seat.id === 't2p2Name'
                            ? '3px'
                            : undefined
                      }
                      placeholder={seat.placeholder}
                      playerName={formik.values[seat.id]}
                      errors={formik.errors[seat.id]}
                      touched={formik.touched[seat.id]}
                      handleChange={formik.handleChange}
                    />
                  </Box>
                );
              })}
            </DealerHandoff>

            {showFirstDealer && (
              // Centred in the felt between the side seats' fields and the
              // YOU text, so it never meets a seat or the print at any table
              // height.
              <Flex
                position="absolute"
                left={0}
                right={0}
                {...HOME_LOWER_BAND}
                align="center"
                justify="center"
                pointerEvents="none"
              >
                <Flex
                  data-testid="home-dealer-hint"
                  align="center"
                  gap="var(--spades-spacing-xs)"
                  whiteSpace="nowrap"
                  fontSize="var(--spades-font-size-2xs)"
                  color="var(--spades-mint)"
                  pointerEvents="auto"
                >
                  <DealerTag
                    id={firstDealer}
                    isCurrent
                    roundHistory={roundHistory}
                    names={formik.values}
                    setup={dealerSetup}
                    hint
                  />
                </Flex>
              </Flex>
            )}
            <HomeFeltPrint
              hasGameData={hasGameData}
              round={roundHistory.length + 1}
            />
          </TableSurface>

          {/* Equal columns, never fixed widths, or the pair would overlap
              below 390pt. A grid rather than flex: the Primary and Secondary
              borders differ, and flex-basis 0 leaves the keys 2px apart. */}
          <Grid
            data-testid="home-actions"
            mt="var(--spades-spacing-md)"
            px="var(--spades-spacing-md)"
            gap="var(--spades-spacing-sm)"
            templateColumns={
              hasGameData || newGameSetup
                ? 'minmax(0, 1fr) minmax(0, 1fr)'
                : 'minmax(0, 1fr)'
            }
          >
            {hasGameData ? (
              <>
                <Button
                  variant="secondary"
                  size="lg"
                  {...trackProps('new_game_requested', { source: 'home' })}
                  onPointerDown={() => {
                    void loadWarningModal();
                  }}
                  onClick={handleNewGame}
                  data-cy="newGameButton"
                  type="button"
                >
                  New game
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  data-cy="continueButton"
                  type="submit"
                >
                  Continue game
                </Button>
              </>
            ) : (
              <>
                {newGameSetup && (
                  <Button
                    variant="secondary"
                    type="button"
                    aria-label="Cancel new game"
                    onClick={() => {
                      clearNameDraft();
                      navigate('/spades-calculator');
                    }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="lg"
                  type="submit"
                  data-cy="startButton"
                  onPointerDown={() => {
                    void loadScoreLimitModal();
                  }}
                >
                  Start game
                </Button>
              </>
            )}
          </Grid>
        </Box>
      </FitStage>
      {/* Room to scroll a low seat above the keyboard. Below everything,
          so nothing on screen moves. */}
      {keyboardRoom > 0 && (
        <Box
          aria-hidden="true"
          data-testid="keyboard-room"
          h={`${keyboardRoom}px`}
        />
      )}
    </form>
  );

  return (
    <>
      {/* Always mounted, so a modal opened in a transition waits for its
          code here instead of showing a fallback (handleNewGame). */}
      <Suspense fallback={null}>
        {isWarningModalOpen && (
          <WarningModal
            analyticsSource="home"
            isOpen={isWarningModalOpen}
            setIsModalOpen={setIsWarningModalOpen}
            resetNames={setNames}
          />
        )}
        {isScoreLimitModalOpen && (
          <ScoreLimitModal
            isOpen={isScoreLimitModalOpen}
            setIsModalOpen={setIsScoreLimitModalOpen}
            onDecline={() => finishGameStart(null)}
            onSetLimit={(limit) => finishGameStart(limit)}
          />
        )}
      </Suspense>
      {hasSetupRail ? (
        // D1: the stage on the left, Game setup in the right rail (W4).
        <WideColumns
          stage={form}
          rail={
            <GameSetupRail
              value={setupLimit}
              onDigit={pressSetupDigit}
              onBackspace={pressSetupBackspace}
              onPreset={(limit) =>
                setSetup({ limit: String(limit), preset: true })
              }
              onNoLimit={() => {
                setSetup({ limit: '', preset: false });
                trackEvent('score_limit_declined', { context: 'setup' });
              }}
            />
          }
        />
      ) : (
        form
      )}
    </>
  );
}

export default NameForm;
